import React, { useEffect, useRef, useState } from 'react';
import { Button, message, Modal, Spin, Tooltip, Typography } from 'antd';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { GridApi } from 'ag-grid-community';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../main/hooks';
import { setSurveyMode } from './IStoredSurveySlice';
import { MODE } from './IStoredSurvey';
import { PARTNER_TYPE, SELECTED_GROUP_ID } from '../../constants';
import { SURVEY_OVERVIEW_TABLE_HEADERS } from './SurveyUtils';
import { getAgGridLocalization } from '../info-portal/components/grid/GridUtil';
import { SurveyOverviewFilter } from '__generated__/globalTypes';
import { SurveyCreateRequest } from '../../../__generated__/globalTypes';
import { CopySurveyRequest } from '../../__generated__/types';
import { IAppComponentProps } from '../../components';
import { useCreateSurveyFromMasterSurveyMutation } from './mutations.generated';
import { getSurveyList_getSurveyList } from './__generated__/getSurveyList';
import client from '../../config/GraphQLApolloClient';
import { ALL_SURVEY_OVERVIEW_FOR_LOGGED_IN_USER_QUERY } from './queries';

const getSurveyOverviewData = async (
    surveyOverviewFilter: SurveyOverviewFilter
): Promise<getSurveyList_getSurveyList[]> => {
    const surveyStatusIds = [12, undefined];

    const queries = surveyStatusIds.map((statusId) => {
        const filter: SurveyOverviewFilter = {
            ...surveyOverviewFilter,
            surveyStatusId: statusId,
        };

        // Only include companyCode for statusId 12
        if (statusId !== 12) {
            delete filter.companyCode;
        }

        return client
            .query({
                query: ALL_SURVEY_OVERVIEW_FOR_LOGGED_IN_USER_QUERY,
                variables: {
                    surveyOverviewFilter: filter,
                    pageNumber: 1,
                    pageSize: 1_000,
                },
                fetchPolicy: 'network-only',
            })
            .then((response) => {
                return { statusId, response };
            });
    });

    const responses: getSurveyList_getSurveyList[] = [];
    for (const query of queries) {
        const queryResult = await query;
        let surveyList = queryResult.response.data?.getSurveyList || [];
        if (queryResult.statusId !== 12) {
            surveyList = surveyList.filter((survey) => survey.surveyStatusId !== 12);
        }
        responses.push(...surveyList);
    }

    return responses;
};

type CopyMasterSurveyModalProps = {
    visible: boolean;
    onClose: () => void;
    selectedSurvey: getSurveyList_getSurveyList | null;
    currentUser: IAppComponentProps['currentUser'];
    onSearchRequest?: (groupId: string) => void;
} & IAppComponentProps;

function filterSurveys(data: getSurveyList_getSurveyList[], selectedSurvey: getSurveyList_getSurveyList) {
    // Manual filtering based on company codes for suppliers and customers
    let filteredData = data;
    if (selectedSurvey.categoryCode === 'V') { // Supplier category
        const selectedCompanyCode = selectedSurvey.companyCode;
        let allowedCompanyCodes: string[] = [];

        // Define filtering rules based on selected survey's company code
        if (['A1', 'MU', 'B7'].includes(selectedCompanyCode)) {
            allowedCompanyCodes = ['A1', 'MU', 'B7', 'C5', 'S5', 'U0', 'LY', 'RK'];
        } else if (selectedCompanyCode === 'C5') {
            allowedCompanyCodes = ['C5'];
        } else if (selectedCompanyCode === 'S5') {
            allowedCompanyCodes = ['S5'];
        } else if (selectedCompanyCode === 'U0') {
            allowedCompanyCodes = ['U0'];
        } else if (selectedCompanyCode === 'LY') {
            allowedCompanyCodes = ['LY'];
        } else if (selectedCompanyCode === 'RK') {
            allowedCompanyCodes = ['RK'];
        } else if (['H9', 'T1', 'T2'].includes(selectedCompanyCode)) {
            allowedCompanyCodes = ['H9', 'T1', 'T2'];
        } else if (['LG', 'FE'].includes(selectedCompanyCode)) {
            allowedCompanyCodes = ['LG', 'FE'];
        } else if (selectedCompanyCode === 'UH') {
            allowedCompanyCodes = ['UH'];
        }

        // Apply the filtering
        if (allowedCompanyCodes.length > 0) {
            filteredData = data.filter(item => allowedCompanyCodes.includes(item.companyCode));
        }
    } else if (selectedSurvey.categoryCode === 'C') { // Customer category
        const selectedCompanyCode = selectedSurvey.companyCode;
        let allowedCompanyCodes: string[] = [];

        // Define filtering rules based on selected survey's company code for customers
        if (['A1', 'EK'].includes(selectedCompanyCode)) {
            allowedCompanyCodes = ['A1', 'EK'];
        } else if (['C6', 'F4'].includes(selectedCompanyCode)) {
            allowedCompanyCodes = ['C6', 'F4'];
        } else if (['H9', 'T1', 'T2'].includes(selectedCompanyCode)) {
            allowedCompanyCodes = ['H9', 'T1', 'T2'];
        } else if (selectedCompanyCode === 'A1D') {
            allowedCompanyCodes = ['A1D'];
        }

        // Apply the filtering
        if (allowedCompanyCodes.length > 0) {
            filteredData = data.filter(item => allowedCompanyCodes.includes(item.companyCode));
        }
    }

    const disallowedStatuses = [10, 9, 5];

    filteredData = filteredData.filter(item => !disallowedStatuses.includes(item.surveyStatusId));

    return filteredData.filter(item =>
        (item.surveyStatusId === 12 && item.companyCode === selectedSurvey.companyCode) || item.agreementType === selectedSurvey.agreementType);
}

const CopyMasterSurveyModal: React.FunctionComponent<CopyMasterSurveyModalProps> = ({
    visible,
    onClose,
    selectedSurvey,
    currentUser,
    distinctEntitlements,
    onSearchRequest
}) => {
    const intl = useIntl();
    const [modalData, setModalData] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>(undefined);
    const selectedGridRowsRef = useRef<getSurveyList_getSurveyList[]>([]);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Apollo mutation hook
    const [createSurveyFromMasterSurvey, { loading: mutationLoading }] = useCreateSurveyFromMasterSurveyMutation();

    const fetchModalData = async () => {
        if (!selectedSurvey || !visible) return;
        
        setLoading(true);
        try {
            // Create filter based on selected survey to get related data
            const surveyOverviewFilter: SurveyOverviewFilter = {
                // Only filter by partner type, we'll do manual filtering for company codes
                companyCode: selectedSurvey.companyCode,
                partnerType: selectedSurvey.categoryCode,
            };

            const data = await getSurveyOverviewData(surveyOverviewFilter);

            setModalData(filterSurveys(data, selectedSurvey));
        } catch (error) {
            console.error('Error fetching modal data:', error);
            setModalData([]);
        } finally {
            setLoading(false);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        params.api.sizeColumnsToFit();
    };

    const onRowSelected = () => {
        if (gridApi) {
            // keep selected rows in a ref to avoid unnecessary re-renders
            selectedGridRowsRef.current = gridApi.getSelectedRows();
        }
    };

    const handleModalSubmit = async () => {
        if (selectedGridRowsRef.current.length === 0) {
            message.error(intl.formatMessage({
                id: 'copy-master-survey-no-selection-error',
                defaultMessage: 'Please select one or more surveys before submitting the onboarding request.',
            }));
            return;
        }

        if (!selectedSurvey) {
            return;
        }

        try {
            // Transform selected rows to SurveyCreateRequest format
            const listSurveyCreateRequest: SurveyCreateRequest[] = selectedGridRowsRef.current.map(row => ({
                category: row.categoryCode,
                companyCode: row.companyCode,
                partnerNo: row.partnerNo,
                systemCode: row.systemCode,
                surveyTypeId: 1, // Default EINV type
                surveyStatusId: row.surveyStatusId,
                surveyId: row.surveyId,
                groupId: row.groupId
            }));

            const copySurveyRequest: CopySurveyRequest = {
                masterSurveyId: selectedSurvey.surveyId,
                listSurveyCreateRequest
            };

            const response = await createSurveyFromMasterSurvey({
                variables: {
                    copySurveyRequest
                }
            });

            if (response.data?.createSurveyFromMasterSurvey) {
                const results = response.data.createSurveyFromMasterSurvey;
                const hasErrors = results.some(result => result.success === '0');
                
                if (hasErrors) {
                    const errorMessages = results
                        .filter(result => result.success === '0')
                        .map(result => result.errorText)
                        .join('\n');
                    message.error(errorMessages);
                } else {
                            // Extract groupId from the first successful result
                            const groupId = results[0]?.groupId;

                            // Helper: determine partner type from master survey category
                    const determinePartnerType = (): string => selectedSurvey?.categoryCode || 'C';
                    // Helper: perform post-create flow (store ids, set mode, navigate)
                    const applyPostCreateFlow = (gid: string) => {
                        try {
                            localStorage.setItem(SELECTED_GROUP_ID, gid);
                            const partnerType = determinePartnerType();
                            localStorage.setItem(PARTNER_TYPE, partnerType);
                            // mark source so the wizard knows we came from COPY_SURVEY
                            sessionStorage.setItem('source', 'COPY_SURVEY');
                            dispatch(setSurveyMode(MODE.EDIT));
                            // Close modal first to avoid overlay during navigation
                            onClose();
                            navigate('/survey/survey-master-data');
                        } catch (err) {
                            console.error('Error during post-create flow:', err);
                            message.error('Failed to complete post-create actions. Please try again.');
                        }
                    };

                            if (groupId) {
                                if (onSearchRequest) {
                                    try { onSearchRequest(groupId); } catch (e) {
                                        /* Just print error. Not necessary to cancel process */
                                        console.error('Error during post-create flow:', e);
                                    }
                                }
                                applyPostCreateFlow(groupId);
                            } else {
                                message.error(intl.formatMessage({
                                    id: 'copy-master-survey-no-groupid-error',
                                    defaultMessage: 'Surveys created but no Group ID was returned. Please check the results.',
                                }));
                            }
                }
            }
        } catch (error) {
            console.error('Error submitting onboarding request:', error);
            message.error(intl.formatMessage({
                id: 'copy-master-survey-submit-error',
                defaultMessage: 'Failed to submit onboarding request. Please try again.',
            }));
        }
    };

    useEffect(() => {
        if (visible && selectedSurvey) {
            fetchModalData();
        }
    }, [visible, selectedSurvey]);

    // Deselect all rows when modal opens
    useEffect(() => {
        if (visible) {
            selectedGridRowsRef.current = [];
            if (gridApi) {
                gridApi.deselectAll();
            }
        }
    }, [visible, gridApi]);

    return (
        <Modal
            title={intl.formatMessage({
                id: 'copy-master-survey-modal-title',
                defaultMessage: 'Copy Master Survey',
            })}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1200}
            className="copy-master-survey-modal"
        >
            <div style={{ marginBottom: '20px' }}>
                <Typography.Paragraph>
                    {intl.formatMessage({
                        id: 'copy-master-survey-modal-description',
                        defaultMessage:
                            'The possible partner numbers that can be onboarded in the same way as the Master Survey are displayed here. Please select the partner numbers to be activated and click on Submit Onboarding Request.',
                    })}
                </Typography.Paragraph>
            </div>

            {/* Modal action buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px', marginBottom: '20px' }}>
                <Tooltip
                    title={intl.formatMessage({
                        id: 'copy-master-survey-close-tooltip',
                        defaultMessage: 'Return to Survey Overview',
                    })}
                >
                    <Button icon={<CloseOutlined />} onClick={onClose}>
                        {intl.formatMessage({
                            id: 'copy-master-survey-close-tooltip',
                            defaultMessage: 'Return to Survey Overview',
                        })}
                    </Button>
                </Tooltip>

                <Tooltip
                    title={intl.formatMessage({
                        id: 'copy-master-survey-modal-title',
                        defaultMessage: 'Copy Master Survey',
                    })}
                >
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={handleModalSubmit}
                        loading={mutationLoading}
                    >
                        {intl.formatMessage({
                            id: 'copy-master-survey-modal-title',
                            defaultMessage: 'Copy Master Survey',
                        })}
                    </Button>
                </Tooltip>
            </div>

            {/* Grid for displaying survey data */}
            <Spin spinning={loading}>
                <div className="pl15 ag-theme-balham">
                    <div style={{ flex: 'auto', height: '500px' }}>
                        <AgGridReact
                            defaultColDef={{
                                flex: 1,
                                enableValue: true,
                                sortable: true,
                                resizable: true,
                            }}
                            rowData={modalData}
                            columnDefs={SURVEY_OVERVIEW_TABLE_HEADERS(intl, currentUser, distinctEntitlements)}
                            localeText={getAgGridLocalization(intl)}
                            sideBar={false}
                            rowClass="agGridRows"
                            suppressMenuHide={true}
                            debug={false}
                            pagination={true}
                            rowSelection={'multiple'}
                            paginationPageSize={10}
                            suppressRowClickSelection={false}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </div>
            </Spin>
        </Modal>
    );
};

export default CopyMasterSurveyModal;
